// utils/learning/progressStore.js
// ★ 存储 key 由 kgid 改为 kgid + '_' + level（学段隔离，杜绝不同学段同名 kgid 串数据）

const KEY = 'kg_learning_progress';
const sep = '_'; // 若要求纯拼接可改为 ''

// ★ 统一生成存储 key
function mkKey(kgid, level) {
  return `${kgid || ''}${sep}${level || ''}`;
}

export const NODE_STATUS = {
  NOT_LEARNED: 'not',
  LEARNED: 'learned',
  MASTERED: 'mastered',
};

const MAX_DIAG_RECORDS = 50;

// ---------- 基础读写 ----------
function loadAll() {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; }
  catch { return {}; }
}

function saveAll(all) {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch (e) {
    try {
      Object.values(all).forEach(g => {
        (g.diagnosisRecords || []).forEach(r => { r.report = ''; });
      });
      localStorage.setItem(KEY, JSON.stringify(all));
      console.warn('[progressStore] 本地存储已满，已清空报告缓存重试');
    } catch (e2) {
      console.warn('[progressStore] 保存失败:', e2);
    }
  }
}

// ★ 内部统一入口：传入 kgid + level，内部转 key
function getGraph(kgid, level) {
  return loadAll()[mkKey(kgid, level)] || null;
}

function getOrCreateGraph(kgid, level) {
  const all = loadAll();
  const k = mkKey(kgid, level);
  if (!all[k]) all[k] = { visitedNodes: {}, diagnosisRecords: [] };
  if (!all[k].visitedNodes) all[k].visitedNodes = {};
  if (!all[k].diagnosisRecords) all[k].diagnosisRecords = [];
  return { all, g: all[k] };
}

// ---------- 节点学习状态 ----------
export function markNodeLearned(kgid, level, nodeName) {
  if (!kgid || !level || !nodeName) return;
  const { all, g } = getOrCreateGraph(kgid, level);
  const cur = g.visitedNodes[nodeName];
  if (!cur || cur.status === NODE_STATUS.NOT_LEARNED) {
    g.visitedNodes[nodeName] = { status: NODE_STATUS.LEARNED, time: Date.now() };
    saveAll(all);
  }
}

export function markNodeMastered(kgid, level, nodeName) {
  if (!kgid || !level || !nodeName) return;
  const { all, g } = getOrCreateGraph(kgid, level);
  g.visitedNodes[nodeName] = { status: NODE_STATUS.MASTERED, time: Date.now() };
  saveAll(all);
}

export function getNodeStatus(kgid, level, nodeName) {
  return getGraph(kgid, level)?.visitedNodes?.[nodeName]?.status || NODE_STATUS.NOT_LEARNED;
}

// ---------- 图谱总节点数 ----------
export function setGraphNodeTotal(kgid, level, total) {
  if (!kgid || !level || !total) return;
  const { all, g } = getOrCreateGraph(kgid, level);
  if (!g.nodeTotal || total > g.nodeTotal) {
    g.nodeTotal = total;
    saveAll(all);
  }
}

// ---------- 诊断会话 ----------
export function startDiagSession(kgid, level, path) {
  const { all, g } = getOrCreateGraph(kgid, level);
  g.diagSession = {
    path: (path || '').replace(/诊断$/, ''),
    step: 0,
    answers: [],
    startTime: Date.now(),
  };
  saveAll(all);
  return g.diagSession;
}

export function getDiagSession(kgid, level) {
  return getGraph(kgid, level)?.diagSession || null;
}

export function recordDiagAnswer(kgid, level, answerPayload) {
  const all = loadAll();
  const g = all[mkKey(kgid, level)];
  if (!g || !g.diagSession) return null;
  const s = g.diagSession;
  s.step += 1;
  s.answers.push({ step: s.step, payload: answerPayload, time: Date.now() });
  saveAll(all);
  return s;
}

export function finishDiagSession(kgid, level, summary, refChatId = '', reportText = '') {
  const all = loadAll();
  const g = all[mkKey(kgid, level)];
  if (!g || !g.diagSession) return null;
  const s = g.diagSession;
  const record = {
    id: `diag_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    time: Date.now(),
    status: 'done',
    path: s.path,
    questions: s.step,
    duration: Date.now() - s.startTime,
    summary: summary || '',
    refChatId: refChatId || '',
    report: reportText || '',
  };
  g.diagnosisRecords.unshift(record);
  if (g.diagnosisRecords.length > MAX_DIAG_RECORDS) {
    g.diagnosisRecords.length = MAX_DIAG_RECORDS;
  }
  delete g.diagSession;
  saveAll(all);
  return record;
}

export function abortDiagSession(kgid, level, reason) {
  const all = loadAll();
  const g = all[mkKey(kgid, level)];
  if (!g || !g.diagSession) return;
  const s = g.diagSession;
  if (s.step > 0) {
    g.diagnosisRecords.unshift({
      id: `diag_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      time: Date.now(),
      status: 'partial',
      path: s.path,
      questions: s.step,
      duration: Date.now() - s.startTime,
      summary: reason || '中断',
      refChatId: '',
      report: '',
    });
    if (g.diagnosisRecords.length > MAX_DIAG_RECORDS) {
      g.diagnosisRecords.length = MAX_DIAG_RECORDS;
    }
  }
  delete g.diagSession;
  saveAll(all);
}

// ---------- 诊断记录读取 ----------
export function getDiagnosisRecord(kgid, level, recordId) {
  return getGraph(kgid, level)?.diagnosisRecords?.find(r => r.id === recordId) || null;
}

export function cacheReport(kgid, level, recordId, reportText) {
  if (!reportText) return;
  const all = loadAll();
  const r = all[mkKey(kgid, level)]?.diagnosisRecords?.find(x => x.id === recordId);
  if (!r) return;
  r.report = reportText;
  saveAll(all);
}

// ---------- 统计 ----------
export function getGraphStats(kgid, level, allNodeNames = []) {
  const g = getGraph(kgid, level) || { visitedNodes: {}, diagnosisRecords: [] };
  const learned = Object.values(g.visitedNodes || {})
    .filter(n => n.status === NODE_STATUS.LEARNED).length;
  const mastered = Object.values(g.visitedNodes || {})
    .filter(n => n.status === NODE_STATUS.MASTERED).length;
  const total = g.nodeTotal || allNodeNames.length || (learned + mastered) || 0;
  return {
    visitedNodes: g.visitedNodes || {},
    diagnosisRecords: g.diagnosisRecords || [],
    hasActiveSession: !!g.diagSession,
    activeSession: g.diagSession || null,
    learned,
    mastered,
    total,
    percent: total ? Math.round(((learned + mastered) / total) * 100) : 0,
  };
}

// ---------- 诊断记录删除 ----------
export function deleteDiagnosisRecord(kgid, level, recordId) {
  if (!kgid || !level || !recordId) return false;
  const all = loadAll();
  const g = all[mkKey(kgid, level)];
  if (!g || !g.diagnosisRecords?.length) return false;
  const before = g.diagnosisRecords.length;
  g.diagnosisRecords = g.diagnosisRecords.filter(r => r.id !== recordId);
  if (g.diagnosisRecords.length === before) return false;
  saveAll(all);
  return true;
}

export function clearDiagnosisRecords(kgid, level) {
  if (!kgid || !level) return false;
  const all = loadAll();
  const g = all[mkKey(kgid, level)];
  if (!g || !g.diagnosisRecords?.length) return false;
  g.diagnosisRecords = [];
  saveAll(all);
  return true;
}

// ---------- ★ 导出 / 导入 ----------
// 导出：把 localStorage 中该 key 下的所有图谱数据打包成 JSON 字符串
// （含 visitedNodes、diagnosisRecords（含 report 全文）、diagSession、nodeTotal 等全部元数据）
export function exportAllProgress() {
  const all = loadAll();
  return JSON.stringify({
    version: 1,
    exportedAt: Date.now(),
    graphs: all,
  }, null, 2);
}

// 导入：恢复数据。mode:
//   'merge'  — 与现有数据合并（同名图谱，记录去重、visitedNodes 按时间新者胜）
//   'replace'— 清空后整体替换
export function importAllProgress(jsonText, mode = 'merge') {
  let parsed;
  try {
    parsed = JSON.parse(jsonText);
  } catch (e) {
    return { ok: false, msg: '文件格式错误，不是合法 JSON' };
  }
  const graphs = parsed?.graphs;
  if (!graphs || typeof graphs !== 'object') {
    return { ok: false, msg: '缺少 graphs 数据字段' };
  }

  if (mode === 'replace') {
    saveAll(graphs);
    return { ok: true, count: Object.keys(graphs).length };
  }

  const all = loadAll();
  let merged = 0;
  Object.entries(graphs).forEach(([key, incoming]) => {
    if (!incoming || typeof incoming !== 'object') return;
    const cur = all[key];
    if (!cur) {
      all[key] = incoming;
      merged++;
      return;
    }
    // 合并 visitedNodes：时间新的优先
    const vn = { ...(cur.visitedNodes || {}) };
    Object.entries(incoming.visitedNodes || {}).forEach(([name, st]) => {
      const old = vn[name];
      if (!old || (st?.time || 0) >= (old?.time || 0)) vn[name] = st;
    });
    // 合并 diagnosisRecords：按 id 去重，按时间倒序
    const map = new Map();
    [...(cur.diagnosisRecords || []), ...(incoming.diagnosisRecords || [])]
      .forEach(r => {
        const exist = map.get(r.id);
        if (!exist || (r.time || 0) > (exist.time || 0)) map.set(r.id, r);
      });
    const records = [...map.values()].sort((a, b) => b.time - a.time).slice(0, MAX_DIAG_RECORDS);
    
    // ★ 闯关数据合并（新增）
    // gameNodes：逐节点按时间新者胜，与 visitedNodes 同策略
    const gn = { ...(cur.gameNodes || {}) };
    Object.entries(incoming.gameNodes || {}).forEach(([name, st]) => {
      const old = gn[name];
      if (!old || (st?.time || 0) >= (old?.time || 0)) gn[name] = st;
    });
    // gameRecords：按 id 去重 + 时间倒序，上限复用 MAX_DIAG_RECORDS
    const gameMap = new Map();
    [...(cur.gameRecords || []), ...(incoming.gameRecords || [])]
      .forEach(r => {
        const exist = gameMap.get(r.id);
        if (!exist || (r.time || 0) > (exist.time || 0)) gameMap.set(r.id, r);
      });
    const gameRecords = [...gameMap.values()].sort((a, b) => b.time - a.time).slice(0, MAX_DIAG_RECORDS);

    all[key] = {
      ...cur,
      ...incoming,
      visitedNodes: vn,
      diagnosisRecords: records,
      // ★ 新增：闯关节点标记 + 闯关历史记录
      gameNodes: gn,
      gameRecords,
      // 保留未被打断的进行中会话（若导入方有则以新为准）
      diagSession: incoming.diagSession || cur.diagSession,
      // ★ 新增：进行中的闯关会话，同样"导入方有则以新为准"
      gameSession: incoming.gameSession || cur.gameSession,
      nodeTotal: Math.max(cur.nodeTotal || 0, incoming.nodeTotal || 0) || undefined,
    };
    merged++;
  });
  saveAll(all);
  return { ok: true, count: merged };
}

// ---------- ★ 新增：删除某图谱的全部本地数据 ----------
// 删除图谱时调用：清除该 (kgid, level) 的学习进度、节点总数、诊断记录等
export function removeGraphProgress(kgid, level) {
  if (!kgid || !level) return false;
  const all = loadAll();
  const k = mkKey(kgid, level);
  if (!all[k]) return false;
  delete all[k];
  saveAll(all);
  return true;
}

// ---------- ★ 闯关游戏（独立体系，不复用 visitedNodes / diagnosisRecords） ----------
// 图对象新增三个字段：gameSession（进行中）、gameRecords（历史）、gameNodes（节点闯关标记）

export const GAME_STATUS = { SUCCESS: 'game_success', FAIL: 'game_fail' };

export function startGameSession(kgid, level, path) {
  const { all, g } = getOrCreateGraph(kgid, level);
  g.gameSession = { path: (path || '').replace(/闯关$/, ''), step: 0, startTime: Date.now() };
  saveAll(all);
  return g.gameSession;
}
export function getGameSession(kgid, level) { return getGraph(kgid, level)?.gameSession || null; }

export function recordGameAnswer(kgid, level) {
  const all = loadAll(); const g = all[mkKey(kgid, level)];
  if (!g || !g.gameSession) return null;
  g.gameSession.step += 1;
  saveAll(all);
  return g.gameSession;
}

export function finishGameSession(kgid, level, success, summary = '', refChatId = '', reportText = '') {
  const all = loadAll(); const g = all[mkKey(kgid, level)];
  if (!g || !g.gameSession) return null;
  const s = g.gameSession;
  const record = {
    id: `game_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    time: Date.now(), status: success ? 'done' : 'done', result: success ? 'success' : 'fail',
    path: s.path, questions: s.step, duration: Date.now() - s.startTime,
    summary, refChatId: refChatId || '', report: reportText || '',
  };
  if (!g.gameRecords) g.gameRecords = [];
  g.gameRecords.unshift(record);
  if (g.gameRecords.length > MAX_DIAG_RECORDS) g.gameRecords.length = MAX_DIAG_RECORDS;
  delete g.gameSession;
  saveAll(all);
  return record;
}

export function abortGameSession(kgid, level, reason) {
  const all = loadAll(); const g = all[mkKey(kgid, level)];
  if (!g || !g.gameSession) return;
  const s = g.gameSession;
  if (s.step > 0) {
    if (!g.gameRecords) g.gameRecords = [];
    g.gameRecords.unshift({ id: `game_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      time: Date.now(), status: 'partial', result: 'aborted', path: s.path,
      questions: s.step, duration: Date.now() - s.startTime, summary: reason || '中断', report: '' });
  }
  delete g.gameSession;
  saveAll(all);
}

export function getGameRecords(kgid, level) { return getGraph(kgid, level)?.gameRecords || []; }

// ---------- 节点闯关标记（独立于 visitedNodes） ----------
export function markNodeGame(kgid, level, nodeName, success) {
  if (!kgid || !level || !nodeName) return;
  const { all, g } = getOrCreateGraph(kgid, level);
  if (!g.gameNodes) g.gameNodes = {};
  g.gameNodes[nodeName] = { status: success ? GAME_STATUS.SUCCESS : GAME_STATUS.FAIL, time: Date.now() };
  saveAll(all);
}
export function getNodeGameStatus(kgid, level, nodeName) {
  return getGraph(kgid, level)?.gameNodes?.[nodeName]?.status || '';
}

// ---------- ★ 全局闯关成功次数（跨图谱，localStorage） ----------
const GAME_WIN_KEY = 'kg_game_win_count';
export function getGameWinCount() { return parseInt(localStorage.getItem(GAME_WIN_KEY) || '0', 10) || 0; }
export function incrGameWinCount() {
  const c = getGameWinCount() + 1;
  try { localStorage.setItem(GAME_WIN_KEY, String(c)); } catch (e) {}
  return c;
}

// ---------- 闯关记录删除 ----------
export function deleteGameRecord(kgid, level, recordId) {
  if (!kgid || !level || !recordId) return false;
  const all = loadAll();
  const g = all[mkKey(kgid, level)];
  if (!g || !g.gameRecords?.length) return false;
  const before = g.gameRecords.length;
  g.gameRecords = g.gameRecords.filter(r => r.id !== recordId);
  if (g.gameRecords.length === before) return false;
  saveAll(all);
  return true;
}

// 一键清空全部闯关记录
export function clearGameRecords(kgid, level) {
  if (!kgid || !level) return false;
  const all = loadAll();
  const g = all[mkKey(kgid, level)];
  if (!g || !g.gameRecords?.length) return false;
  g.gameRecords = [];
  saveAll(all);
  return true;
}

